<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { fly } from "svelte/transition";
  import { combineLatest, map, of } from "rxjs";
  import { debounceTime, startWith, switchMap, tap } from "rxjs/operators";

  import { PUBLIC_PNP_IMG_HOST, PUBLIC_PNP_IMGHD_HOST } from "$env/static/public";
  import { ProductType, sortProductsByType, type ProductData } from "$lib/def/landing";

  import {
    DEFAULT_MERCHANT_KEY,
    getCartItem,
    updateCartItemQuantity,
    observeUpdateQtyRequests,
  } from "$lib/serv/groc-cart";
  import { fetchProduct, fetchProductInfo, updateSavedStatus } from "$lib/serv/groc-product";
  import { formatCurrency } from "$lib/util/format";
  import { RxWritable } from "$lib/util/obs";
  import { fetchBackups } from "./server";
  import Product from "../Product.svelte";
  import Modal from "$lib/ui/shared/Modal.svelte";
  import Carousel from "$lib/ui/layout/Carousel.svelte";
  import BackupSelect from "./BackupSelect.svelte";
  import Quantity from "$lib/ui/shared/Quantity.svelte";
  import ToolTip, { ToolPosition } from "$lib/ui/shared/ToolTip.svelte";
  import { setItem } from "$lib/serv/core/local-store";
  import { profile$ } from "$lib/serv/core/auth";
    import CarouselV2 from "$lib/ui/layout/CarouselV2.svelte";

  const dispatch = createEventDispatcher();

  export let product: ProductData;
  $: product$ = of(product);
  $: backup$ = product$.pipe(
    map((p) => p.alternative_item ?? null),
    // tap((v) => console.log("local backup", v?.catalogue_key))
  );

  $: cartProduct$ = getCartItem(product.catalogue_key);
  $: cartQty$ = cartProduct$.pipe(map((cP) => cP?.quantity ?? 0));
  $: cartBackup$ = cartProduct$.pipe(
    startWith(null),
    debounceTime(0),
    map((cP) => cP?.alternative_item ?? null),
    tap((cB) => {
      if (!cB) return;
      // console.log("cart backup", cB);
      const _cB = cB as any;
      _cB.price_now = cB?.price;
      _cB.url = cB?.image_url?.filename ?? "";
      _cB.stock_status_message = "";

      if (product.pristine && cB && !$backup$) dispatch("backup", cB);
    })
  );

  $: cartUpdates$ = observeUpdateQtyRequests(product.catalogue_key).pipe(
    startWith(null),
    tap((cU) => {
      if (cU?.event === "ended") {
        setTimeout(() => dispatch("back"), 400);
        setTimeout(() => setItem('showCart', true), 800);
      }
    })
  );

  $: backupDiff$ = combineLatest([backup$, cartBackup$]).pipe(
    map(([b, cB]) => {
      if (b && !cB) return true;
      if (!b && cB) return true;
      if (b && cB && b.catalogue_key !== cB.catalogue_key) return true;
      return false;
    })
    // tap((v) => console.log("backup diff", v))
  );

  $: qty$ = new RxWritable($cartQty$ || 1);
  $: qtyChanges$ = combineLatest([qty$, cartQty$]).pipe(
    tap(([q, cQ]) => {
      if (q === 0) qty$.set(1);
      if (q === 0 && cQ >= 1) {
        updateCartItemQuantity(
          DEFAULT_MERCHANT_KEY,
          product.catalogue_key,
          0,
          product.alternative_item?.catalogue_key ?? null,
          true
        );
      }
    })
  );
  $qtyChanges$;

  $: disableCartBtn$ = combineLatest([cartQty$, qty$, cartUpdates$, backupDiff$]).pipe(
    map(([cQ, q, updates, bDiff]) => {
      if (bDiff) return false;

      return (
        !product.in_stock ||
        (cQ > 0 && cQ === q) ||
        ["started", "started-unchanged"].includes(updates?.event ?? "")
      );
    })
  );
  $: cartBtnTxt$ = combineLatest([cartQty$, qty$, backupDiff$]).pipe(
    map(([cQ, q, bDiff]) => {
      if (!product.in_stock) return "Sold out";
      if (cQ === 0) return `Add to cart`;
      if (cQ === q && bDiff) return "Update cart";
      if (cQ === q) return "Added to cart";
      return `Update cart`;
    })
  );

  type Tab = "info" | "ingredients" | "nutrition" | "";
  const tabs: Tab[] = ["info", "ingredients", "nutrition"];
  let tab: Tab = "";
  const setTab = (v: Tab) => (tab = tab === v ? "" : v);

  const toggleSave = () =>
    updateSavedStatus(product, product.user_specific?.saved_list ? "unsave" : "save").subscribe({
      next: (p) => (product = p),
      // NB ^^^^^^^^^^^^^^^^^^^^
      error: (err) => console.error("failed to save product", err),
      complete: () => console.log("save product complete"),
    });

  const search$ = new RxWritable("");
  const search$$ = search$.pipe(debounceTime(1000));

  $: info$ = product$.pipe(switchMap((p) => fetchProductInfo(p.catalogue_key)));

  $: linked$ = product$.pipe(switchMap((p) => fetchProduct(p.catalogue_key))).pipe(
    // tap((product) => console.log({ product })),
    map((p) => ({
      linked_combos: sortProductsByType(p?.linked_combos ?? [], []),
      linked_products: sortProductsByType(p?.linked_products ?? [], []),
    }))
    // tap((linked) => console.log({ linked }))
  );

  $: backups$ = fetchBackups(product, $search$$);
  let showBackupModal = false;
  $: console.log('[product.alternative_item, ...$backups$.items as ProductData[]]', product.alternative_item, $backups$?.items);
</script>

<div id="container" data-testid="pdp-container">
  <button class="pdp-back-btn" on:click={() => dispatch("back")}>
    <span class="icon-chevron-left-20" />Back
  </button>

  {#if $profile$}
    <button id="like-btn" on:click={toggleSave}>
      <span class={`icon-heart${product.user_specific?.saved_list ? "-filled-" : "-"}28`} />
      <ToolTip message={product.user_specific?.saved_list ? "Saved" : "Save"} pos={ToolPosition.Left} />
    </button>
  {/if}

  <div id="product" class:sold-out={!product.in_stock}>
    <div id="image" style="background-image: url({`${PUBLIC_PNP_IMGHD_HOST}/${product.url}`});">
      {#if !product.in_stock}
        <div id="sold-out">
          <span class="label-s bold same-line">Sold out</span>
        </div>
      {/if}
    </div>

    <div id="info">
      {#if product.product_type === ProductType.Combo}
        <div id="tags">
          <!--<span class="body-m bold combo-tag m-0" class:sold-out={!$product.in_stock}>Combo</span>-->
          <span class="body-m bold orange-tag m-0" class:sold-out={!product.in_stock}>Combo</span>
          <button id="deals-tag" class="body-m bold grey m-0" on:click={() => dispatch("close")}>
            More Deals <span class="icon-chevron-right-20" />
          </button>
        </div>
      {/if}

      <div id="pricing">
        <!-- pricing, stock, etc -->
        {#if product.price_was}
          <span class="headline-s medium red m-0">{formatCurrency(product.price_now)}</span>
          <span class="headline-s book m-0 grey"><s>{formatCurrency(product.price_was)}</s></span>
        {:else}
          <span class="headline-s book m-0">{formatCurrency(product.price_now)}</span>
        {/if}
        {#if product.stock_status_message}
          <span class="label-xl medium grey m-0">{product.stock_status_message}</span>
        {/if}
      </div>

      <div id="description">
        <!-- description, tags, etc -->
        <h3 class="display-m bold">{product.label}</h3>
        {#if product.is_alcohol}
          <div class="flex-row align-center">
            <span class="icon-18-plus-20" />
            <span class="label-xl book m-0">Not for sale to persons under 18</span>
          </div>
          {#if product.alcohol_delivery_message}
            <div class="flex-row align-center">
              <span class="icon-clock-20" />
              <span class="label-xl book m-0">{product.alcohol_delivery_message}</span>
            </div>
          {/if}
        {/if}
        {#if product.variable_weight}
          <div class="label-m book">{product.uom_label}</div>
        {/if}
        {#if product.vitality_flag}
          <div class="label-m book">Vitality Healthy Dining</div>
        {/if}
        {#if product.product_type === ProductType.Combo}
          <p class="body-m book">{product.description}</p>
        {/if}
        {#if product.lifestyle_endorsements?.primary || product.lifestyle_endorsements?.secondary}
          <div id="endorsements">
            {#each [...(product.lifestyle_endorsements?.primary ?? []), ...(product.lifestyle_endorsements?.secondary ?? [])] as end}
              <span class="body-s book grey">{end}</span>
            {/each}
          </div>
        {/if}
      </div>

      <div id="controls-container">
        <div id="controls">
          <div id="qty-container">
            <Quantity bind:quantity={$qty$} min={!$cartQty$ ? 1 : 0} max={product.maximum_order_quantity} debounce={false} />
          </div>
          <button
            id="cart-btn"
            class="button"
            disabled={$disableCartBtn$}
            on:click={() =>
              updateCartItemQuantity(
                DEFAULT_MERCHANT_KEY,
                product.catalogue_key,
                $qty$,
                product.alternative_item?.catalogue_key ?? null,
                true
              )}
          >
            <span class="headline-s bold">{$cartBtnTxt$}</span>
          </button>
        </div>
      </div>

      {#if product.product_type === ProductType.Single}
        <div id="silo">
          <div id="pills">
            {#each tabs as t}
              <button class="label-xl" class:selected={tab === t} on:click={() => setTab(t)}>
                <span style="text-transform: capitalize;">{t}</span>
                <span class={tab === t ? "icon-chevron-up-20" : "icon-chevron-down-20"} />
              </button>
            {/each}
          </div>
          <div id="body" in:fly|global={{ x: 100, delay: 50 }}>
            {#if tab === "info" || tab === "ingredients"}
              {#if $info$ && $info$[tab].length > 0}
                {#each $info$[tab] as obj}
                  <div class="info-container">
                    <h4 class="headline-s bold">{obj.label}</h4>
                    {#if obj.view_type === "list"}
                      <ul>
                        {#each obj.data as str}
                          <li>{str}</li>
                        {/each}
                      </ul>
                    {:else}
                      <p class="body-m book">{obj.data[0]}</p>
                    {/if}
                  </div>
                {/each}
              {:else}
                <span class="grey">No {tab} available</span>
              {/if}
            {:else if tab === "nutrition"}
              {#if $info$ && $info$.nutrition.length > 0}
                <table id="nutrition">
                  <thead>
                    <tr><th>Per 100g/ml</th><th>Per serving(250)</th></tr>
                  </thead>
                  {#each $info$.nutrition as obj}
                    <tr class="main">
                      <td>{obj.title}</td>
                      <td>{obj.per_100}</td>
                      <td>{obj.per_serving}</td>
                    </tr>
                    {#if obj.sub_level}
                      {#each obj.sub_level as sub}
                        <tr class="sub">
                          <td>{sub.title}</td>
                          <td>{sub.per_100}</td>
                          <td>{sub.per_serving}</td>
                        </tr>
                      {/each}
                    {/if}
                  {/each}
                </table>
              {:else}
                <span class="grey">No nutrition available</span>
              {/if}
            {/if}
          </div>
        </div>
      {/if}

      {#if !product.disable_backup && $backups$}
        {@const { loading, items:payload, error } = $backups$}
        <div id="desktop-backup-carousel" class="backup-carousel no-scroll">
          <CarouselV2
            cache={{ loading, payload, error }}
            key={(v) => !!v ? (product.alternative_item ? [product.alternative_item, ...v as ProductData[]] : v) as ProductData[] : []}>
            {#snippet title()}
              <!-- <div class="fill-space">
                <div class="title"> -->

                  <h4 class="headline-m bold ellipses" style="margin-bottom: 1rem;">
                    Pick a backup in case it’s sold out
                  </h4>
                  <div class="backup-selection margin-bottom">
                    <article>
                      <button
                        class="backupCheck"
                        on:click={() => (product.alternative_item ? dispatch("backup", null) : null)}
                      >
                        <div class:checked={!product.alternative_item}>
                          {#if !product.alternative_item}
                            <span class="icon-check-20" />
                          {/if}
                        </div>
                        <div>
                          <p class="bold">No backup</p>
                          <span>
                            {#if !product.alternative_item}
                              We'll refund you if it's sold out
                            {:else}
                              Refund me if it's sold out
                            {/if}
                          </span>
                        </div>
                      </button>
                      <button class="backupSearch" on:click={() => dispatch("showBackupModal")}>
                        <span class="icon-search-20" />
                      </button>
                    </article>
                  </div>
                <!-- </div>
              </div> -->
            {/snippet}
            {#snippet children({ item })}
              <Product
                {item}
                asBackup={true}
                isSelected={item?.catalogue_key === product?.alternative_item?.catalogue_key}
                on:product={(e) => dispatch("backup", e.detail)} />
            {/snippet}
          </CarouselV2>
        </div>
      {/if}
    </div>
  </div>
</div>

{#if $linked$?.linked_combos.length > 0}
  <Carousel cache={{ loading: false, payload: $linked$.linked_combos }} type="aisle" let:item key={(v) => v}>
    <div slot="title" class="flex mobile flex-nowrap title">
      <h4 class="headline-m bold ellipses">Get the combo deal</h4>
    </div>
    <Product {item} on:product={(e) => dispatch("detail", e.detail)} />
  </Carousel>
{/if}

{#if $linked$?.linked_products.length > 0}
  <div class="separator-line" />
  <Carousel
    cache={{ loading: false, payload: $linked$.linked_products }}
    type="aisle"
    let:item
    key={(v) => v}
  >
    <div slot="title" class="flex mobile flex-nowrap title">
      <h4 class="headline-m bold ellipses">In this deal</h4>
    </div>
    <Product {item} on:product={(e) => dispatch("detail", e.detail)} />
  </Carousel>
{/if}

<style lang="scss" global>
  @use "screen";
  @use "color";
  @use 'margin';
  
  #container {
    padding-top: 2.5rem;
  
    @media (screen.$max-md) {
      margin-bottom: 140px;
      padding-top: 32px;
    }
  
    @media (screen.$max-sm) {
      padding-top: 42px;
    }
  }
  
  .separator-line {
    width: 100%;
    height: 8px;
    background: color.$separator;
    opacity: .4;
  }
  
  :global(#like-btn) {
  	position: absolute;
  	top: 64px;
  	right: 16px;
    background-color: color.$modal-button-bg;
  	padding: 5px;
  	border-radius: 50%;
  	transition: all .2s ease;
  
    &:hover {
      cursor: pointer;
    }
  
    // &.saved {
    //   background-color: color.$on-surface;
  
    //   span {
    //     color: color.$surface;
    //   }
    // }
  }
  
  span[class*="icon-"] {
    display: flex;
    align-items: center;
    justify-content: center;
  
    border-radius: inherit;
  }
  
  #product {
    // background-color: blue;
    display: flex;
    margin-bottom: 32px;
  
    @media (screen.$max-md) {
      flex-direction: column;
    }
  
    #image {
      position: relative;
  
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
      border-radius: 25px;
  
      height: 300px;
      width: 300px;
      margin-right: 32px;
  
      @media (screen.$max-sm) {
        height: 250px;
        width: 250px;
        margin-bottom: 16px;
      }
    }
  
    #sold-out {
      display: flex;
      align-items: center;
      justify-content: center;
  
      height: 100%;
      width: 100%;
  		background-color: color.transparent(0.7);
      border-radius: inherit;
  
      span {
        background: color.$white;
        color: color.$on-primary;
        padding: 4px 14px;
        border: 1px solid color.$on-primary;
        border-radius: 100px;
      }
    }
  
    #info {
      // background-color: grey;
      flex: 1;
      max-width: 80%;
  
      @media (screen.$max-md) {
        max-width: 100%;
      }
    }
  
    #tags {
      position: relative;
      display: flex;
  
      span {
        // Only tested with combo tag - the below in not needed - if we run into issues, we can get back to this
        // position: static;
        // display: flex;
        // align-items: center;
        // padding: 0 8px;
        &:not(:last-child) {
          margin-right: 8px;
        }
  
        span {
          padding: 0;
        }
      }
  
      #deals-tag {
        display: flex;
        align-items: center;
        border-radius: 25px;
        // padding: 4px 8px;
        margin-left: 5rem;
        @media(screen.$lg) { margin-left: 4rem; }
        &:hover {
          background-color: color.$object-elevation-3dp;
          cursor: pointer;
        }
      }
    }
  
    #pricing {
      display: flex;
      align-items: center;
  
      span:not(:last-child) {
        margin-right: 8px;
      }
    }
  
    #description {
      @include margin.tiny;
      h3, div { @include margin.tiny; }
  
      span[class*="icon-18"],
      span[class*="icon-clock"] {
        margin-right: 8px;
      }
  
      #endorsements {
        span:not(:last-child) {
          &:after {
            content: "·";
            margin: 0 5px;
          }
        }
      }
    }
  
    #controls-container {
      display: flex;
      flex-direction: column;
      @media (screen.$max-md) {
        position: fixed;
        bottom: 0;
        left: 0;
  
        display: flex;
        justify-content: space-evenly;
  
        background-color: color.$background-elevation-12dp;
        margin-bottom: 0;
        padding: 0 18px;
        padding-top: 12px;
        width: 100%;
        z-index: 3;
      }
    }

    // #mobile-select-backup {
    //   display: none;
    //   margin-bottom: 12px;
    //   @media (screen.$max-md) {
    //     display: flex;
    //     align-items: center;
    //     justify-content: space-between;
    //   }
    //   // #selected-backup {
    //   //   display: flex;
    //   //   align-items: center;
    //   //   #img {
    //   //     background-repeat: no-repeat;
    //   //     background-position: center;
    //   //     background-size: cover;
  
    //   //     border-radius: 12px;
    //   //     height: 50px;
    //   //     width: 50px;
    //   //     margin-right: 12px;
    //   //   }
    //   // }
    //   button {
    //     color: color.$primary;
    //   }
    // }
  
    #controls {
      display: flex;
      align-items: center;
      margin-bottom: 1.5rem;
  
      @media (screen.$max-md) {
        display: flex;
        justify-content: space-between;
      }
  
      #qty-container {
        margin-right: 12px;
  
        // #max-msg {
        //   border: 2px solid color.$object-elevation-3dp;
        //   border-radius: 25px;
  
        //   margin-bottom: 8px;
        //   padding: 2px 8px;
        //   text-align: center;
        // }
  
        // #qty-control {
        //   display: flex;
        //   align-items: center;
  
        //   border: 2px solid color.$object-elevation-3dp;
        //   border-radius: 35px;
  
        //   padding: 4px 6px;
  
        //   &.disabled {
        //     border: 2px solid color.$object-elevation-3dp;
        //     color: color.$object-elevation-3dp;
        //   }
  
        //   button {
        //     background-color: initial;
  
        //     &:disabled {
        //       span {
        //         color: color.$object-elevation-3dp;
        //       }
        //     }
  
        //     &:hover {
        //       background-color: color.$object-elevation-3dp;
        //     }
  
        //     &.circle {
        //       height: 45px;
        //       width: 45px;
        //       border-radius: 50%;
        //     }
        //   }
  
        //   span {
        //     margin: 0 8px;
        //   }
        // }
      }
  
      #cart-btn {
        padding: 14px 24px;
        @media(screen.$xxs) { padding: 14px 34px; }
      }
    }
  
    #silo {
      #pills {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-bottom: 16px;
        button {
          display: flex;
          align-items: center;
          border: 2px solid color.$separator;
          border-radius: 25px;
          padding: 8px 12px 9px 12px;
          margin: 0 0 .5rem 0;
          &:hover {
            background-color: color.$object-elevation-3dp;
            cursor: pointer;
          }
  
          &:not(:last-child) {
            margin-right: 8px;
          }
  
          &.selected {
            background-color: color.$on-surface;
            color: color.$surface;
          }
  
          @media (screen.$max-sm) {
            padding: 4px 6px;
          }
  
          span {
            margin-left: 8px;
          }
        }
      }
  
      #body {
        padding-bottom: 1rem;
        .info-container {
          margin-bottom: 20px;
          h4 { line-height: 1.15em }
          h4, p { @include margin.tiny; }
          ul { list-style-position: inside; }
        }
        
  
        table#nutrition {
          border-collapse: collapse;
          width: 100%;
  
          th {
            font-weight: 700;
          }
  
          th,
          td {
            text-align: left;
            padding: 2px 0;
          }
  
          tr {
            border-bottom: 1px solid color.$object-elevation-6dp;
  
            &:nth-child(1) {
              border-bottom: 1px solid color.$on-surface;
            }
          }
  
          tr.main {
            padding: 0px;
          }
  
          tr.sub {
            td:nth-child(1) {
              padding-left: 16px;
            }
          }
        }
      }
    }
  }
  
  #desktop-backup-carousel {
    background: color.$object-elevation-3dp;
    padding: 1rem;
    border-radius: 2rem;
    @media (screen.$md) {
      padding: 2rem;
    }
    .backup-selection {
      >article {
        display: flex;
        justify-content: space-between;
        button.backupCheck { 
          display: flex;
          align-items: center;
          cursor: pointer;
          div {
            &:nth-of-type(1) {
              width: 20px;
              height: 20px;
              border: 2px solid color.$separator;
              border-radius: 4px;
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
              &.checked {
                background: color.$primary-variant;
                border: 2px solid color.$primary-variant;
              }
            }
            &:nth-of-type(2) {
              margin-left: .75rem;
              p { text-align: left; margin-bottom: 0; }
              span { color: color.$on-background-medium-importance; }
            }
          }
        }
        button.backupSearch {
          width: 40px;
          height: 40px;
          cursor: pointer;
          border: 2px solid color.$separator;
          border-radius: 50%;
          @media(screen.$md) { transform: translateY(3px); }
          @media(screen.$lg) { transform: translateY(8px); }
        }
      }
    }
    // @media (screen.$max-md) { display: none; }
  }
</style>
